import { Component } from '@angular/core';
import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TabelaComponent } from '../../shared/tabela/tabela.component';
import { FonteNotaInterface } from '../../shared/rodape-fonte-nota/fonte-nota.interface';
import { RodapeFonteNotaComponent } from '../../shared/rodape-fonte-nota/rodape-fonte-nota.component';
import {
  CondicaoQualidadeStateService,
  MemoriaDeCalculoService,
  LocalizacaoService,
  AcessoOfertaEducacionalStateService
} from '../../services';
import { MemoriaDeCalculoRequestDto} from '../../dtos/memoria-calculo-dto'
import { MemoriaDeCalculoDto } from '../../dtos';
import { QuantitativoDocentesService } from '../../services/quantitativo-docentes/quantitativo-docentes.service';
import { NumeroTotalDocentesDto } from '../../dtos/numero-total-docentes.dto';
import { AnosReferenciasService } from '../../services/anos-referencias/anos-referencias.service';
import { AnosReferenciasDto } from '../../dtos/anos-referencias.dto';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

interface ProjecaoNumeroProfessores {
  etapa: string;
  projecao: number;
}

interface NumeroProfessoresPorEtapa {
  projecoes: ProjecaoNumeroProfessores[];
  totalProfessores: number;
  totalPorEtapa: number;
}

@Component({
  selector: 'app-numero-professores',
  standalone: true,
  imports: [
    MatIconModule,
    TabelaComponent,
    NgFor,
    NgIf,
    RodapeFonteNotaComponent,
    DecimalPipe
  ],
  templateUrl: './numero-professores.component.html',
  styleUrl: './numero-professores.component.scss',
})
export class NumeroProfessoresComponent {
  dadosTabela: NumeroProfessoresPorEtapa | undefined;
  ano: number | null = null;
  anosReferencias: AnosReferenciasDto | null = null;
  anoCenso: number;
  anoPqr: number;

  cabecalhoTabela = [
    { titulo: 'Etapa', tamanho: '34%' },
    {
      titulo: 'Número de professores em 2025 <sup>(1)(2)</sup>',
      tamanho: '28%',
    },
    {
      titulo: 'Projeção do número necessário de professores <sup>(3)</sup>',
      tamanho: '38%',
      subtitulos: ['2026'],
    },
  ];

  notasFonte: FonteNotaInterface[] = [
    {
      titulo: 'Fontes',
      conteudos: [
        {
          numero: 1,
          descricao:
            'Elaborado a partir dos microdados do Censo Escolar/INEP 2025.',
        },
      ],
    },
    {
      titulo: 'Notas',
      conteudos: [
        {
          numero: 2,
          descricao:
            'A contagem do número de professores considera os profissionais informados no Censo Escolar nas funções de ‘Docente’ e de ‘Docente Titular’ - coordenador de tutoria (de módulo ou disciplina) – EaD. São considerados apenas os professores que atuam em turmas cujo tipo de atendimento não seja de Atividade Complementar ou de Atendimento Educacional Especializado (AEE). Os docentes são contados uma única vez em cada Município, portanto, o total não representa a soma das etapas de ensino, pois o mesmo docente pode atuar em mais de uma unidade de agregação.',
        },
        {
          numero: 3,
          descricao:
            'O Total de professores refere-se a soma das etapas, sempre arredondado para cima.',
        },
      ],
    },
  ];

  memoriaCalculoDto: MemoriaDeCalculoDto | undefined;
  numeroTotalDocentes: NumeroTotalDocentesDto | undefined;

  constructor(
    private memoriaCalculoService: MemoriaDeCalculoService,
    private condicaoQualidadeStateService: CondicaoQualidadeStateService,
    private acessoEOfertaStateService: AcessoOfertaEducacionalStateService,
    private quantitativoDocentesService: QuantitativoDocentesService,
    private anosReferenciasService: AnosReferenciasService,
    private featureFlagService: FeatureFlagService
  ) {
    if (this.featureFlagService.funcionalidadeHabilitada('940-atualizacao-de-componentes-de-edicao-pqr')) {
      this.anosReferencias = this.anosReferenciasService.obterAnosReferenciasSnapshot();
    }
    this.anoCenso = this.anosReferencias?.anoCenso ?? 2025;
    this.anoPqr = this.anosReferencias?.anoPqr ?? 2026;
  }

  async ngOnInit() {

    await this.recalcularMemoriaComDadosEditados();

    this.numeroTotalDocentes = await this.quantitativoDocentesService.obterPorLocalizacao();
    this.ano = this.numeroTotalDocentes.ano;

    this.cabecalhoTabela[1].titulo = `Número de professores em ${this.ano ?? this.anoCenso} <sup>(1)(2)</sup>`;
    this.cabecalhoTabela[2].subtitulos = [String(this.anoPqr)];
    this.notasFonte[0].conteudos[0].descricao = `Elaborado a partir dos microdados do Censo Escolar/INEP ${this.anoCenso}.`;

    this.preenchaInformacoesTabela();
  }

  private async recalcularMemoriaComDadosEditados(): Promise<void> {
    try {
      this.memoriaCalculoDto = await this.memoriaCalculoService.recalcularMemoriaDeCalculo();
    } catch (error) {
      console.error('Erro ao recalcular memória:', error);
      this.memoriaCalculoDto = await this.memoriaCalculoService.buscarMemoriaDeCalculoLocalizacao();
    }
  }

  preenchaInformacoesTabela() {
    const crecheIdadeHabilitada = this.featureFlagService.funcionalidadeHabilitada('1104-creche-idade');
    const resultadoCalcNumProf = this.memoriaCalculoDto?.resultadoCalcNumProf;
    const resultadoEtapa10 = crecheIdadeHabilitada
      ? resultadoCalcNumProf
      : this.memoriaCalculoDto?.resultadoEtapa10CalculoDemandaPorDocentes;

    const numeroProfessoresPorEtapa: NumeroProfessoresPorEtapa = {
      projecoes: [],
      totalProfessores: this.numeroTotalDocentes?.quantidade || 0,
      totalPorEtapa: resultadoEtapa10?.total ?? 0
    }

    const creche: number = crecheIdadeHabilitada
      ? (resultadoCalcNumProf?.creche0UrbanaParcial ?? 0) +
        (resultadoCalcNumProf?.creche0UrbanaIntegral ?? 0) +
        (resultadoCalcNumProf?.creche0RuralParcial ?? 0) +
        (resultadoCalcNumProf?.creche0RuralIntegral ?? 0) +
        (resultadoCalcNumProf?.creche1UrbanaParcial ?? 0) +
        (resultadoCalcNumProf?.creche1UrbanaIntegral ?? 0) +
        (resultadoCalcNumProf?.creche1RuralParcial ?? 0) +
        (resultadoCalcNumProf?.creche1RuralIntegral ?? 0) +
        (resultadoCalcNumProf?.creche2UrbanaParcial ?? 0) +
        (resultadoCalcNumProf?.creche2UrbanaIntegral ?? 0) +
        (resultadoCalcNumProf?.creche2RuralParcial ?? 0) +
        (resultadoCalcNumProf?.creche2RuralIntegral ?? 0) +
        (resultadoCalcNumProf?.creche3UrbanaParcial ?? 0) +
        (resultadoCalcNumProf?.creche3UrbanaIntegral ?? 0) +
        (resultadoCalcNumProf?.creche3RuralParcial ?? 0) +
        (resultadoCalcNumProf?.creche3RuralIntegral ?? 0)
      : (resultadoEtapa10?.crecheRuralIntegral ?? 0) +
        (resultadoEtapa10?.crecheRuralParcial ?? 0) +
        (resultadoEtapa10?.crecheUrbanaIntegral ?? 0) +
        (resultadoEtapa10?.crecheUrbanaParcial ?? 0);
    numeroProfessoresPorEtapa.projecoes.push({
      etapa: 'Creche',
      projecao: creche
    })

    const preEscola: number = (resultadoEtapa10?.preEscolaRuralIntegral ?? 0) +
      (resultadoEtapa10?.preEscolaRuralParcial ?? 0) +
      (resultadoEtapa10?.preEscolaUrbanaIntegral ?? 0) +
      (resultadoEtapa10?.preEscolaUrbanaParcial ?? 0);
    numeroProfessoresPorEtapa.projecoes.push({
      etapa: 'Pré-Escola',
      projecao: preEscola
    })

    const ensFunAnosIniciais: number = (resultadoEtapa10?.ensFunAnosIniciaisRuralIntegral ?? 0) +
      (resultadoEtapa10?.ensFunAnosIniciaisRuralParcial ?? 0) +
      (resultadoEtapa10?.ensFunAnosIniciaisUrbanaIntegral ?? 0) +
      (resultadoEtapa10?.ensFunAnosIniciaisUrbanaParcial ?? 0);
    numeroProfessoresPorEtapa.projecoes.push({
      etapa: 'Ensino Fundamental - anos iniciais',
      projecao: ensFunAnosIniciais
    })

    const ensFunAnosFinais: number = (resultadoEtapa10?.ensFunAnosFinaisRuralIntegral ?? 0) +
      (resultadoEtapa10?.ensFunAnosFinaisRuralParcial ?? 0) +
      (resultadoEtapa10?.ensFunAnosFinaisUrbanaIntegral ?? 0) +
      (resultadoEtapa10?.ensFunAnosFinaisUrbanaParcial ?? 0);
    numeroProfessoresPorEtapa.projecoes.push({
      etapa: 'Ensino Fundamental - anos finais',
      projecao: ensFunAnosFinais
    })

    const ensinoMedio: number = (resultadoEtapa10?.ensMedioRuralIntegral ?? 0) +
      (resultadoEtapa10?.ensMedioRuralParcial ?? 0) +
      (resultadoEtapa10?.ensMedioUrbanaIntegral ?? 0) +
      (resultadoEtapa10?.ensMedioUrbanaParcial ?? 0);
    numeroProfessoresPorEtapa.projecoes.push({
      etapa: 'Ensino Médio',
      projecao: ensinoMedio
    })

    const eja: number = crecheIdadeHabilitada
      ? (resultadoCalcNumProf?.ejaUrbana ?? 0) +
        (resultadoCalcNumProf?.ejaRural ?? 0)
      : (resultadoEtapa10?.ejaRuralParcial ?? 0) +
        (resultadoEtapa10?.ejaUrbanaParcial ?? 0);
    numeroProfessoresPorEtapa.projecoes.push({
      etapa: 'EJA',
      projecao: eja
    })

    this.dadosTabela = numeroProfessoresPorEtapa;
  }
}
